import { DesktopIcon } from '@/components/icons';
import useAuth from '@/data/hook/useAuth';
import useStudentContext from '@/data/hook/useStudentContext';
import { isStaff, isStudent } from '@/functions/auth';
import i18n from '@/lib/i18n';
import { UserTypeEnum } from '@/models/User';
import { getAge } from '@/utils/profile';
import { ROUTES } from '@/utils/routes';
import {
  AcademicCapIcon,
  ChatBubbleBottomCenterTextIcon,
  BellIcon,
  CalendarIcon,
  CogIcon,
  RectangleStackIcon,
  IdentificationIcon,
  BuildingLibraryIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

export interface Option {
  testId: string;
  title: string;
  onClick?: (params?: any) => void;
  icon: JSX.Element;
  url: string;
  roles?: UserTypeEnum[];
  condition?: boolean;
}

const defaultWitdh = 'h-5 w-5';

export default function useOptions() {
  const { user } = useAuth();

  const { currentProgress, extraCoursesStats, otherCoursesStats } =
    useStudentContext();

  const hasUser = !!user;

  const isStaffUser = hasUser && isStaff(user.userType);

  const isStudentUser = hasUser && isStudent(user.userType);

  const hasOtherCourses = isStudentUser && otherCoursesStats.length > 0;

  const coursesCondition =
    isStaffUser || (isStudentUser && !!currentProgress && hasOtherCourses);

  const age = user?.profile?.dateOfBirth && getAge(user.profile.dateOfBirth);

  const hasExtraCourses = isStudentUser && extraCoursesStats.length > 0;

  const showExtraCourses = hasExtraCourses && !!age && age > 13;

  const options: Option[] = [
    {
      testId: 'sidebarHome',
      title: i18n.t('sideMenu.home'),
      icon: <Squares2X2Icon className={defaultWitdh} />,
      url: '/',
    },
    {
      testId: 'sidebarCourses',
      title: i18n.t('sideMenu.courses'),
      icon: <AcademicCapIcon className={defaultWitdh} />,
      url: '/courses',
      condition: coursesCondition,
    },
    {
      testId: 'sidebarExtraCourses',
      title: i18n.t('sideMenu.extraCourses'),
      icon: <DesktopIcon className={defaultWitdh} />,
      url: '/extra-courses',
      roles: [UserTypeEnum.STUDENT],
      condition: showExtraCourses,
    },
    {
      testId: 'sidebarProjects',
      title: i18n.t('sideMenu.projects'),
      icon: <RectangleStackIcon className={defaultWitdh} />,
      url: '/projects',
      roles: [
        UserTypeEnum.SUPER_ADMIN,
        UserTypeEnum.UNIT_ADMIN,
        UserTypeEnum.TEACHER,
        UserTypeEnum.STUDENT,
      ],
    },
    {
      testId: 'sidebarFeedback',
      title: i18n.t('sideMenu.feedback'),
      icon: <ChatBubbleBottomCenterTextIcon className={defaultWitdh} />,
      roles: [
        UserTypeEnum.TEACHER,
        UserTypeEnum.UNIT_ADMIN,
        UserTypeEnum.SUPER_ADMIN,
      ],
      url: ROUTES.LESSONS_FEEDBACK.BASE,
    },
    {
      testId: 'sidebarCalendar',
      title: i18n.t('sideMenu.calendar'),
      icon: <CalendarIcon className={defaultWitdh} />,
      roles: [
        UserTypeEnum.TEACHER,
        UserTypeEnum.SUPER_ADMIN,
        UserTypeEnum.UNIT_ADMIN,
        UserTypeEnum.PARENT,
      ],
      url: '/calendar',
    },
    {
      testId: 'sidebarNotifications',
      title: i18n.t('sideMenu.notifications'),
      icon: <BellIcon className={defaultWitdh} />,
      roles: [
        UserTypeEnum.SUPER_ADMIN,
        UserTypeEnum.UNIT_ADMIN,
        UserTypeEnum.TEACHER,
      ],
      url: ROUTES.NOTIFICATIONS.BASE,
    },
    {
      testId: 'sidebarHowItWorks',
      title: i18n.t('sideMenu.howWorks'),
      icon: <BuildingLibraryIcon className={defaultWitdh} />,
      roles: [UserTypeEnum.PARENT],
      url: '/como-funciona',
    },
    {
      testId: 'sidebarAdmin',
      title: i18n.t('sideMenu.admin'),
      icon: <IdentificationIcon className={defaultWitdh} />,
      roles: [UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN],
      url: '/admin',
    },
    {
      testId: 'sidebarTeacher',
      title: i18n.t('sideMenu.teacher'),
      icon: <IdentificationIcon className={defaultWitdh} />,
      roles: [UserTypeEnum.TEACHER],
      url: '/teacher',
    },
    {
      testId: 'sidebarConfig',
      title: i18n.t('sideMenu.config'),
      icon: <CogIcon className={defaultWitdh} />,
      url: ROUTES.ACCOUNT.BASE,
    },
  ];
  return options;
}
